import { useEffect, useRef, useState } from "react";
import { Download, Share2 } from "lucide-react";
import { toast } from "sonner";

const SUBJECT = { dogs: "My dog is", cats: "My cat is", both: "My pets are" };

function drawPaw(ctx, x, y, size, color, angle = 0) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(0, size * 0.35, size * 0.62, size * 0.5, 0, 0, Math.PI * 2);
  ctx.fill();
  [[-0.62, -0.25, 0.26], [-0.22, -0.55, 0.28], [0.22, -0.55, 0.28], [0.62, -0.25, 0.26]].forEach(([dx, dy, r]) => {
    ctx.beginPath();
    ctx.arc(dx * size, dy * size, r * size, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.restore();
}

function drawCard(canvas, petType) {
  const S = 1080;
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#FDFBF7";
  ctx.fillRect(0, 0, S, S);
  ctx.strokeStyle = "#4A6B53";
  ctx.lineWidth = 14;
  ctx.strokeRect(36, 36, S - 72, S - 72);
  drawPaw(ctx, 150, 200, 40, "#E3EAE3", -0.4);
  drawPaw(ctx, 930, 260, 52, "#F3E4DE", 0.5);
  drawPaw(ctx, 180, 880, 55, "#F3E4DE", 0.3);
  drawPaw(ctx, 910, 850, 42, "#E3EAE3", -0.5);
  ctx.textAlign = "center";
  ctx.fillStyle = "#4A6B53";
  ctx.font = "600 34px Georgia, serif";
  ctx.fillText("H O L A   M O V E R S", S / 2, 160);
  drawPaw(ctx, S / 2, 330, 78, "#C87961");
  ctx.fillStyle = "#2A2624";
  ctx.font = "300 82px Georgia, serif";
  ctx.fillText(SUBJECT[petType] || SUBJECT.both, S / 2, 530);
  ctx.font = "500 96px Georgia, serif";
  ctx.fillStyle = "#4A6B53";
  ctx.fillText("Mérida-ready", S / 2, 645);
  ctx.fillStyle = "#59534D";
  ctx.font = "30px Georgia, serif";
  ctx.fillText("Pet move checklist complete — Mexico, here we come!", S / 2, 740);
  ctx.font = "italic 28px Georgia, serif";
  ctx.fillStyle = "#9B948C";
  ctx.fillText("They're family. They move like it.", S / 2, 810);
  ctx.fillStyle = "#C87961";
  ctx.font = "600 34px Georgia, serif";
  ctx.fillText("holamovers.com/pets", S / 2, 950);
}

export const PetShareCard = ({ petType = "both" }) => {
  const canvasRef = useRef(null);
  const [url, setUrl] = useState("");
  const shareUrl = `${window.location.origin}/pets`;
  const shareText = `${SUBJECT[petType] || SUBJECT.both} Mérida-ready 🐾 We're moving to Mexico! Get your free pet move checklist:`;

  useEffect(() => {
    drawCard(canvasRef.current, petType);
    setUrl(canvasRef.current.toDataURL("image/png"));
  }, [petType]);

  const download = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "merida-ready.png";
    a.click();
  };

  const nativeShare = async () => {
    try {
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], "merida-ready.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: `${shareText} ${shareUrl}` });
        return;
      }
    } catch { /* fallthrough */ }
    await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
    toast.success("Share text copied — download the card and post it!");
  };

  const btn = "inline-flex items-center gap-2 rounded-full border border-sand-300 bg-sand-100 px-4 py-2 text-sm font-medium text-charcoal-700 hover:border-terracotta hover:text-terracotta transition-colors";

  return (
    <div data-testid="pet-share-card" className="mt-8 rounded-2xl border border-sand-300 bg-sand-100 p-5">
      <p className="text-sm font-medium uppercase tracking-wide text-terracotta">Show off your paw-gress</p>
      <p className="mt-1 text-sm text-charcoal-600">Share your "Mérida-ready" card with friends — and if they move with us, our referral program pays you $100.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-[200px_1fr] sm:items-center">
        <canvas ref={canvasRef} data-testid="pet-share-canvas" className="w-full max-w-[200px] rounded-xl border border-sand-300 shadow-sm" />
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="share-card-download" onClick={download} className={btn}>
            <Download size={15} /> Download card
          </button>
          <button type="button" data-testid="share-card-native" onClick={nativeShare} className={btn}>
            <Share2 size={15} /> Share
          </button>
          <a data-testid="share-card-whatsapp" target="_blank" rel="noreferrer" className={btn}
            href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}>WhatsApp</a>
          <a data-testid="share-card-facebook" target="_blank" rel="noreferrer" className={btn}
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`}>Facebook</a>
          <a data-testid="share-card-x" target="_blank" rel="noreferrer" className={btn}
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}>X</a>
        </div>
      </div>
    </div>
  );
};
